/**
 * SOFSEK — the seven relational reports carried over from the legacy westlock
 * "Reports" menu, rebuilt as read-only cross-tabs over the new access model:
 *   User Authorization · User Roles · Role Actions · User Actions ·
 *   Role Users · User Conflicts · Policy Conflicts
 *
 * Each report resolves every FK to a display name at fetch time (§17.B4) and
 * hands pre-resolved rows to <SfkReportShell>, which owns the shared toolbar +
 * personalization + grid. No writes — reports are read-only.
 */
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiDb } from '../../../lib/api_client';
import { useActiveLocale } from '../../../lib/use_active_locale';
import { formatDate } from '../../../lib/format_date';
import type { AuthSession } from '../../../types/auth';
import { SfkReportShell, tlName } from './SfkReportShell';
import type { ReportRow, ReportColumn, ReportFacetDef } from './SfkReportShell';

// ── tiny resolution helpers (avoid `any`, keep nested access readable) ────────
type Dict = Record<string, unknown>;
const asArr = (v: unknown): Dict[] => (v ?? []) as unknown as Dict[];
const obj = (v: unknown): Dict => (v ?? {}) as Dict;
const str = (v: unknown): string => (v == null ? '' : String(v));
const tlOf = (v: unknown, loc: string) => tlName(v as { locale_code: string; display_text: string }[] | undefined, loc);

async function appMap(locale: string): Promise<Map<number, string>> {
  const { data, error } = await apiDb.from('csm_application_master').select('application_id, internal_code, csm_application_master_tl ( locale_code, display_text )');
  if (error) throw error;
  const m = new Map<number, string>();
  for (const r of asArr(data)) m.set(r.application_id as number, tlOf(r.csm_application_master_tl, locale) || str(r.internal_code));
  return m;
}

async function codeMap(table: string, codeCol: string, tlRel: string, locale: string): Promise<Map<string, string>> {
  const { data, error } = await apiDb.from(table).select(`${codeCol}, ${tlRel} ( locale_code, display_text )`);
  if (error) throw error;
  const m = new Map<string, string>();
  for (const r of asArr(data)) m.set(str(r[codeCol]), tlOf(r[tlRel], locale) || str(r[codeCol]));
  return m;
}

/** Resolve action (atomic-permission) labels by permission_id (= csm_page_master
 *  page_id) through sfk_v_action — actions are now catalog rows, so they are read
 *  in a second step rather than embedded on the platform table. */
async function actionMap(permIds: number[]): Promise<Map<number, { display_name: string; platform_signature: string }>> {
  const m = new Map<number, { display_name: string; platform_signature: string }>();
  const ids = [...new Set(permIds.filter(id => id != null))];
  if (ids.length === 0) return m;
  const { data, error } = await apiDb.from('sfk_v_action').select('permission_id, display_name, platform_signature').in('permission_id', ids);
  if (error) throw error;
  for (const r of asArr(data)) m.set(r.permission_id as number, { display_name: str(r.display_name), platform_signature: str(r.platform_signature) });
  return m;
}

/** Fetch EVERY row of a report query, paging past PostgREST's max_rows cap.
 *  A report must be complete (it is viewed/exported in full), so we range-page in
 *  1000-row windows (each window <= the cap, so none is silently truncated) until
 *  a short page signals the end. The build callback must apply a STABLE order
 *  (unique key, or a unique tiebreaker) so pages don't overlap or skip rows. */
const REPORT_PAGE = 1000;
async function fetchAllPaged(
  build: (from: number, to: number) => PromiseLike<{ data: unknown; error: { message: string } | null }>,
): Promise<Dict[]> {
  const all: Dict[] = [];
  for (let from = 0; ; from += REPORT_PAGE) {
    const { data, error } = await build(from, from + REPORT_PAGE - 1);
    if (error) throw error;
    const chunk = asArr(data);
    all.push(...chunk);
    if (chunk.length < REPORT_PAGE) break;
  }
  return all;
}

// ── shared data loaders ───────────────────────────────────────────────────────

/** role_id → { name, application_id }, from the sfk_external_role compat view
 *  (roles folded into csm_role_master — 20260901000000; the view is a plain
 *  select, so it resolves without a PostgREST embed of a view). */
async function roleMap(): Promise<Map<number, { name: string; application_id: number | null }>> {
  const { data } = await apiDb.from('sfk_external_role').select('role_id, name, application_id');
  return new Map(((data ?? []) as { role_id: number; name: string; application_id: number | null }[])
    .map(r => [r.role_id, { name: r.name, application_id: r.application_id }]));
}

/** Subject ↔ role assignments — powers both "User Roles" and "Role Users". */
async function loadAssignments(locale: string): Promise<ReportRow[]> {
  const apps = await appMap(locale);
  const roles = await roleMap();
  const data = await fetchAllPaged((from, to) => apiDb.from('sfk_app_user_role_assignment').select(`
    assignment_id, is_active, role_id,
    sfk_app_user_account ( external_username, csm_user_master!app_user_id ( display_name ) )
  `).order('assignment_id').range(from, to));
  return data.map(r => {
    const role = roles.get(r.role_id as number);
    const acct = obj(r.sfk_app_user_account);
    return {
      id: str(r.assignment_id),
      user: str(obj(acct.csm_user_master).display_name),
      account: str(acct.external_username),
      application: apps.get(role?.application_id as number) ?? '',
      role: str(role?.name),
      is_active: str(r.is_active),
    };
  });
}

async function loadRoleActions(locale: string): Promise<ReportRow[]> {
  const apps = await appMap(locale);
  const roles = await roleMap();
  const rows = await fetchAllPaged((from, to) => apiDb.from('sfk_role_permission').select(`
    role_permission_id, is_active, permission_id, role_id
  `).order('role_permission_id').range(from, to));
  const actions = await actionMap(rows.map(r => r.permission_id as number));
  return rows.map(r => {
    const role = roles.get(r.role_id as number);
    const perm = actions.get(r.permission_id as number);
    return {
      id: str(r.role_permission_id),
      role: str(role?.name),
      application: apps.get(role?.application_id as number) ?? '',
      action: str(perm?.display_name),
      signature: str(perm?.platform_signature),
      is_active: str(r.is_active),
    };
  });
}

async function loadUserActions(locale: string): Promise<ReportRow[]> {
  const apps = await appMap(locale);
  const rows = await fetchAllPaged((from, to) => apiDb.from('sfk_access_fact').select(`
    access_fact_id, is_active, permission_id,
    sfk_app_user_account ( external_username, application_id, csm_user_master!app_user_id ( display_name ) )
  `).order('access_fact_id').range(from, to));
  const actions = await actionMap(rows.map(r => r.permission_id as number));
  return rows.map(r => {
    const acct = obj(r.sfk_app_user_account);
    const perm = actions.get(r.permission_id as number);
    return {
      id: str(r.access_fact_id),
      user: str(obj(acct.csm_user_master).display_name),
      account: str(acct.external_username),
      application: apps.get(acct.application_id as number) ?? '',
      action: str(perm?.display_name),
      signature: str(perm?.platform_signature),
      is_active: str(r.is_active),
    };
  });
}

async function loadUserConflicts(locale: string): Promise<ReportRow[]> {
  const statuses = await codeMap('sfk_violation_status', 'violation_status_code', 'sfk_violation_status_tl', locale);
  const data = await fetchAllPaged((from, to) => apiDb.from('sfk_violation').select(`
    violation_id, breach_start_at, violation_status_code, app_user_id,
    csm_user_master!app_user_id ( display_name ),
    sfk_conflict_rule ( internal_code, sfk_conflict_rule_tl ( locale_code, display_text ) )
  `).order('breach_start_at', { ascending: false }).order('violation_id').range(from, to));
  // "User Conflicts" = violations held by a subject (vs. a group or a self-user).
  return data.filter(r => r.app_user_id != null).map(r => {
    const rule = obj(r.sfk_conflict_rule);
    return {
      id: str(r.violation_id),
      user: str(obj(r.csm_user_master).display_name),
      policy: tlOf(rule.sfk_conflict_rule_tl, locale) || str(rule.internal_code),
      status: statuses.get(str(r.violation_status_code)) ?? str(r.violation_status_code),
      breach_start: formatDate(str(r.breach_start_at), locale),
    };
  });
}

async function loadPolicyConflicts(locale: string): Promise<ReportRow[]> {
  const statuses = await codeMap('sfk_violation_status', 'violation_status_code', 'sfk_violation_status_tl', locale);
  const data = await fetchAllPaged((from, to) => apiDb.from('sfk_violation').select(`
    violation_id, breach_start_at, violation_status_code, self_user_id,
    csm_user_master!app_user_id ( display_name ),
    sfk_app_user_group ( internal_code, sfk_app_user_group_tl ( locale_code, display_text ) ),
    sfk_conflict_rule ( internal_code, sfk_conflict_rule_tl ( locale_code, display_text ) )
  `).order('violation_id').range(from, to));
  return data.map(r => {
    const rule = obj(r.sfk_conflict_rule);
    const subjName = str(obj(r.csm_user_master).display_name);
    const grp = obj(r.sfk_app_user_group);
    const grpName = tlOf(grp.sfk_app_user_group_tl, locale) || str(grp.internal_code);
    const holder = subjName || grpName || (r.self_user_id != null ? 'Self (SOFSEK user)' : '');
    return {
      id: str(r.violation_id),
      policy: tlOf(rule.sfk_conflict_rule_tl, locale) || str(rule.internal_code),
      holder,
      status: statuses.get(str(r.violation_status_code)) ?? str(r.violation_status_code),
      breach_start: formatDate(str(r.breach_start_at), locale),
    };
  });
}

async function loadUserAuthorization(locale: string): Promise<ReportRow[]> {
  const apps = await appMap(locale);
  const conns = await codeMap('sfk_connection_status', 'connection_status_code', 'sfk_connection_status_tl', locale);
  const data = await fetchAllPaged((from, to) => apiDb.from('sfk_app_user_account').select(`
    app_user_account_id, external_username, application_id, connection_status_code, is_active,
    csm_user_master!app_user_id ( display_name )
  `).order('app_user_account_id').range(from, to));
  return data.map(r => ({
    id: str(r.app_user_account_id),
    user: str(obj(r.csm_user_master).display_name),
    application: apps.get(r.application_id as number) ?? '',
    account: str(r.external_username),
    connection: conns.get(str(r.connection_status_code)) ?? str(r.connection_status_code),
    is_active: str(r.is_active),
  }));
}

// ── shared hook + facet helpers ───────────────────────────────────────────────

function useReport(load: (locale: string) => Promise<ReportRow[]>) {
  const locale = useActiveLocale();
  const [rows, setRows] = useState<ReportRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reload = useCallback(async () => {
    setBusy(true); setError(null);
    try { setRows(await load(locale)); }
    catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
    // load is a stable module-level function; only locale is reactive.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);
  useEffect(() => { void reload(); }, [reload]);
  return { rows, busy, error, reload };
}

function distinctFacet(rows: ReportRow[], key: string, label: string): ReportFacetDef {
  const set = new Set<string>();
  for (const r of rows) { const v = r[key]; if (v) set.add(v); }
  return { key, label, options: [...set].sort().map(v => ({ value: v, label: v })) };
}

// ── report pages ──────────────────────────────────────────────────────────────

export function SfkReportUserAuthorization({ session }: { session: AuthSession }) {
  const { t } = useTranslation();
  const { rows, busy, error, reload } = useReport(loadUserAuthorization);
  const facets = useMemo<ReportFacetDef[]>(() => [
    distinctFacet(rows, 'application', t('sfk.report.facet.app', 'Application')),
    distinctFacet(rows, 'connection', t('sfk.report.facet.connection', 'Connection')),
    { key: 'is_active', label: t('common.boolean.active', 'Active'), options: [{ value: 'Y', label: t('common.boolean.yes', 'Yes') }, { value: 'N', label: t('common.boolean.no', 'No') }] },
  ], [rows, t]);
  const columns: ReportColumn[] = [
    { key: 'user', header: t('sfk.report.col.user', 'User'), width: 200 },
    { key: 'application', header: t('sfk.report.col.application', 'Application'), width: 180 },
    { key: 'account', header: t('sfk.report.col.account', 'Account'), width: 170, kind: 'mono' },
    { key: 'connection', header: t('sfk.report.col.connection', 'Connection'), width: 150 },
    { key: 'is_active', header: t('common.boolean.active', 'Active'), width: 90, kind: 'yesno' },
  ];
  return <SfkReportShell pageCode="SFK.REPORT.USER_AUTHORIZATION" session={session} title={t('sfk.report.user_authorization.title', 'User Authorization')}
    columns={columns} rows={rows} searchKeys={['user', 'account']} facets={facets} busy={busy} error={error} onRefresh={() => void reload()} />;
}

export function SfkReportUserRoles({ session }: { session: AuthSession }) {
  const { t } = useTranslation();
  const { rows, busy, error, reload } = useReport(loadAssignments);
  const facets = useMemo<ReportFacetDef[]>(() => [
    distinctFacet(rows, 'application', t('sfk.report.facet.app', 'Application')),
    { key: 'is_active', label: t('common.boolean.active', 'Active'), options: [{ value: 'Y', label: t('common.boolean.yes', 'Yes') }, { value: 'N', label: t('common.boolean.no', 'No') }] },
  ], [rows, t]);
  const columns: ReportColumn[] = [
    { key: 'user', header: t('sfk.report.col.user', 'User'), width: 200 },
    { key: 'account', header: t('sfk.report.col.account', 'Account'), width: 160, kind: 'mono' },
    { key: 'application', header: t('sfk.report.col.application', 'Application'), width: 180 },
    { key: 'role', header: t('sfk.report.col.role', 'Role'), width: 220 },
    { key: 'is_active', header: t('common.boolean.active', 'Active'), width: 90, kind: 'yesno' },
  ];
  return <SfkReportShell pageCode="SFK.REPORT.USER_ROLES" session={session} title={t('sfk.report.user_roles.title', 'User Roles')}
    columns={columns} rows={rows} searchKeys={['user', 'role', 'account']} facets={facets} busy={busy} error={error} onRefresh={() => void reload()} />;
}

export function SfkReportRoleActions({ session }: { session: AuthSession }) {
  const { t } = useTranslation();
  const { rows, busy, error, reload } = useReport(loadRoleActions);
  const facets = useMemo<ReportFacetDef[]>(() => [
    distinctFacet(rows, 'application', t('sfk.report.facet.app', 'Application')),
    { key: 'is_active', label: t('common.boolean.active', 'Active'), options: [{ value: 'Y', label: t('common.boolean.yes', 'Yes') }, { value: 'N', label: t('common.boolean.no', 'No') }] },
  ], [rows, t]);
  const columns: ReportColumn[] = [
    { key: 'role', header: t('sfk.report.col.role', 'Role'), width: 220 },
    { key: 'application', header: t('sfk.report.col.application', 'Application'), width: 180 },
    { key: 'action', header: t('sfk.report.col.action', 'Action'), width: 220 },
    { key: 'signature', header: t('sfk.report.col.signature', 'Signature'), width: 160, kind: 'mono' },
    { key: 'is_active', header: t('common.boolean.active', 'Active'), width: 90, kind: 'yesno' },
  ];
  return <SfkReportShell pageCode="SFK.REPORT.ROLE_ACTIONS" session={session} title={t('sfk.report.role_actions.title', 'Role Actions')}
    columns={columns} rows={rows} searchKeys={['role', 'action', 'signature']} facets={facets} busy={busy} error={error} onRefresh={() => void reload()} />;
}

export function SfkReportUserActions({ session }: { session: AuthSession }) {
  const { t } = useTranslation();
  const { rows, busy, error, reload } = useReport(loadUserActions);
  const facets = useMemo<ReportFacetDef[]>(() => [
    distinctFacet(rows, 'application', t('sfk.report.facet.app', 'Application')),
    { key: 'is_active', label: t('common.boolean.active', 'Active'), options: [{ value: 'Y', label: t('common.boolean.yes', 'Yes') }, { value: 'N', label: t('common.boolean.no', 'No') }] },
  ], [rows, t]);
  const columns: ReportColumn[] = [
    { key: 'user', header: t('sfk.report.col.user', 'User'), width: 200 },
    { key: 'account', header: t('sfk.report.col.account', 'Account'), width: 160, kind: 'mono' },
    { key: 'application', header: t('sfk.report.col.application', 'Application'), width: 180 },
    { key: 'action', header: t('sfk.report.col.action', 'Action'), width: 220 },
    { key: 'signature', header: t('sfk.report.col.signature', 'Signature'), width: 160, kind: 'mono' },
    { key: 'is_active', header: t('common.boolean.active', 'Active'), width: 90, kind: 'yesno' },
  ];
  return <SfkReportShell pageCode="SFK.REPORT.USER_ACTIONS" session={session} title={t('sfk.report.user_actions.title', 'User Actions')}
    columns={columns} rows={rows} searchKeys={['user', 'action', 'account', 'signature']} facets={facets} busy={busy} error={error} onRefresh={() => void reload()} />;
}

export function SfkReportRoleUsers({ session }: { session: AuthSession }) {
  const { t } = useTranslation();
  const { rows, busy, error, reload } = useReport(loadAssignments);
  const facets = useMemo<ReportFacetDef[]>(() => [
    distinctFacet(rows, 'application', t('sfk.report.facet.app', 'Application')),
    { key: 'is_active', label: t('common.boolean.active', 'Active'), options: [{ value: 'Y', label: t('common.boolean.yes', 'Yes') }, { value: 'N', label: t('common.boolean.no', 'No') }] },
  ], [rows, t]);
  const columns: ReportColumn[] = [
    { key: 'role', header: t('sfk.report.col.role', 'Role'), width: 220 },
    { key: 'application', header: t('sfk.report.col.application', 'Application'), width: 180 },
    { key: 'user', header: t('sfk.report.col.user', 'User'), width: 200 },
    { key: 'account', header: t('sfk.report.col.account', 'Account'), width: 160, kind: 'mono' },
    { key: 'is_active', header: t('common.boolean.active', 'Active'), width: 90, kind: 'yesno' },
  ];
  return <SfkReportShell pageCode="SFK.REPORT.ROLE_USERS" session={session} title={t('sfk.report.role_users.title', 'Role Users')}
    columns={columns} rows={rows} searchKeys={['role', 'user', 'account']} facets={facets} busy={busy} error={error} onRefresh={() => void reload()} />;
}

export function SfkReportUserConflicts({ session }: { session: AuthSession }) {
  const { t } = useTranslation();
  const { rows, busy, error, reload } = useReport(loadUserConflicts);
  const facets = useMemo<ReportFacetDef[]>(() => [
    distinctFacet(rows, 'status', t('sfk.report.facet.status', 'Status')),
  ], [rows, t]);
  const columns: ReportColumn[] = [
    { key: 'user', header: t('sfk.report.col.user', 'User'), width: 220 },
    { key: 'policy', header: t('sfk.report.col.policy', 'Policy'), width: 260 },
    { key: 'status', header: t('sfk.report.col.status', 'Status'), width: 180 },
    { key: 'breach_start', header: t('sfk.report.col.breach_start', 'Breach Start'), width: 140 },
  ];
  return <SfkReportShell pageCode="SFK.REPORT.USER_CONFLICTS" session={session} title={t('sfk.report.user_conflicts.title', 'User Conflicts')}
    columns={columns} rows={rows} searchKeys={['user', 'policy']} facets={facets} busy={busy} error={error} onRefresh={() => void reload()} />;
}

export function SfkReportPolicyConflicts({ session }: { session: AuthSession }) {
  const { t } = useTranslation();
  const { rows, busy, error, reload } = useReport(loadPolicyConflicts);
  const facets = useMemo<ReportFacetDef[]>(() => [
    distinctFacet(rows, 'policy', t('sfk.report.col.policy', 'Policy')),
    distinctFacet(rows, 'status', t('sfk.report.facet.status', 'Status')),
  ], [rows, t]);
  const columns: ReportColumn[] = [
    { key: 'policy', header: t('sfk.report.col.policy', 'Policy'), width: 260 },
    { key: 'holder', header: t('sfk.report.col.holder', 'Holder'), width: 220 },
    { key: 'status', header: t('sfk.report.col.status', 'Status'), width: 180 },
    { key: 'breach_start', header: t('sfk.report.col.breach_start', 'Breach Start'), width: 140 },
  ];
  return <SfkReportShell pageCode="SFK.REPORT.POLICY_CONFLICTS" session={session} title={t('sfk.report.policy_conflicts.title', 'Policy Conflicts')}
    columns={columns} rows={rows} searchKeys={['policy', 'holder']} facets={facets} busy={busy} error={error} onRefresh={() => void reload()} />;
}
